// The control dock along the bottom of the app: ignition, starter, throttle, the
// engine speed from telemetry, and the output volume. Every control is a native
// button or range input, so it works with the keyboard and screen readers; the
// global shortcuts are in keyboard.ts.

import type { ViewStore } from "../../state/store";
import type { AudioSession } from "./audio-session";
import { el, rpmOf } from "./dom";
import type { PowertrainDriver } from "./powertrain-driver";
import { strings } from "./strings";

export interface DockContext {
  store: ViewStore;
  audio: AudioSession;
  driver: PowertrainDriver;
  /** Starts the audio if it is off; call it from the gesture that needs sound. */
  ensureAudio(): void;
}

export function mountDock(root: HTMLElement, ctx: DockContext): () => void {
  const { store, audio, driver } = ctx;
  root.setAttribute("aria-label", strings.dockLabel);

  // Ignition: a switch.
  const ignition = el("button", { type: "button", role: "switch", class: "ctl-switch", "data-control": "ignition", "aria-keyshortcuts": "I" });
  const ignitionState = el("span", { class: "ctl-switch-state" });
  ignition.append(el("span", { class: "ctl-label" }, strings.ignition.label), ignitionState);
  ignition.addEventListener("click", () => {
    const on = !store.state.controls.ignition;
    store.set({ controls: { ignition: on } });
    if (on) ctx.ensureAudio();
  });

  // Starter: held with the pointer, or with Space or Enter while it has focus.
  const starter = el(
    "button",
    { type: "button", class: "ctl-starter", "data-control": "starter", "aria-pressed": "false", "aria-keyshortcuts": "S", title: strings.starter.hint },
    el("span", { class: "ctl-label" }, strings.starter.label),
    el("span", { class: "ctl-hint" }, strings.starter.hint),
  );
  const hold = (held: boolean): void => {
    if (held) ctx.ensureAudio();
    driver.setStarter(held);
  };
  starter.addEventListener("pointerdown", (e) => {
    starter.setPointerCapture(e.pointerId);
    hold(true);
  });
  for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) {
    starter.addEventListener(type, () => hold(false));
  }
  starter.addEventListener("keydown", (e) => {
    if (e.key !== " " && e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation(); // not also a global shortcut
    if (!e.repeat) hold(true);
  });
  starter.addEventListener("keyup", (e) => {
    if (e.key !== " " && e.key !== "Enter") return;
    e.preventDefault();
    hold(false);
  });
  starter.addEventListener("blur", () => hold(false));

  // Throttle: a range in percent.
  const throttleId = "ctl-throttle";
  const throttle = el("input", {
    id: throttleId,
    type: "range",
    min: 0,
    max: 100,
    step: 1,
    "data-control": "throttle",
    "aria-keyshortcuts": "ArrowUp ArrowDown 0",
  });
  const throttleOut = el("output", { for: throttleId, class: "ctl-value" });
  throttle.addEventListener("input", () => store.set({ controls: { throttle: Number(throttle.value) / 100 } }));
  const throttleGroup = el(
    "div",
    { class: "ctl-range ctl-throttle" },
    el("label", { for: throttleId, class: "ctl-label" }, strings.throttle.label),
    throttle,
    throttleOut,
  );

  // Engine speed, from the voice's telemetry.
  const rpmValue = el("span", { class: "ctl-rpm-value", "data-readout": "rpm" }, strings.rpm.none);
  const phase = el("span", { class: "ctl-phase", "data-readout": "phase" });
  const readout = el(
    "div",
    { class: "ctl-readout", role: "group", "aria-label": strings.rpm.label },
    el("span", { class: "ctl-label" }, strings.rpm.label),
    el("span", { class: "ctl-rpm" }, rpmValue, el("span", { class: "ctl-unit" }, ` ${strings.rpm.unit}`)),
    phase,
  );

  // Volume and mute.
  const volumeId = "ctl-volume";
  const volume = el("input", { id: volumeId, type: "range", min: 0, max: 100, step: 1, "data-control": "volume" });
  volume.addEventListener("input", () => store.set({ audio: { volume: Number(volume.value) / 100 } }));
  const mute = el("button", { type: "button", class: "ctl-mute", "data-control": "mute", "aria-keyshortcuts": "M" });
  mute.addEventListener("click", () => store.set({ audio: { muted: !store.state.audio.muted } }));
  const volumeGroup = el(
    "div",
    { class: "ctl-range ctl-volume" },
    el("label", { for: volumeId, class: "ctl-label" }, strings.volume.label),
    volume,
    mute,
  );

  root.append(
    el("div", { class: "dock-group dock-engine" }, ignition, starter),
    el("div", { class: "dock-group dock-throttle" }, throttleGroup),
    el("div", { class: "dock-group dock-readout" }, readout),
    el("div", { class: "dock-group dock-volume" }, volumeGroup),
  );

  const render = (): void => {
    const s = store.state;
    ignition.setAttribute("aria-checked", String(s.controls.ignition));
    ignitionState.textContent = s.controls.ignition ? strings.ignition.on : strings.ignition.off;
    const pct = Math.round(s.controls.throttle * 100);
    if (throttle.value !== String(pct)) throttle.value = String(pct);
    throttleOut.textContent = `${pct} %`;
    throttle.setAttribute("aria-valuetext", `${pct} %`);
    volume.value = String(Math.round(s.audio.volume * 100));
    volume.setAttribute("aria-valuetext", `${Math.round(s.audio.volume * 100)} %`);
    mute.textContent = s.audio.muted ? strings.volume.unmute : strings.volume.mute;
    mute.setAttribute("aria-pressed", String(s.audio.muted));
    starter.setAttribute("aria-pressed", String(driver.starter));
    phase.textContent = audio.status === "on" ? strings.phase[driver.phase] : strings.phase.off;
    phase.dataset["phase"] = audio.status === "on" ? driver.phase : "off";
  };

  // The speed changes every block, so it is read once a frame rather than on events.
  let frame = 0;
  const tick = (): void => {
    const rec = audio.latest;
    const rpm = rec === null ? null : Math.round(rpmOf(rec.omega));
    const text = rpm === null ? strings.rpm.none : String(rpm);
    if (rpmValue.textContent !== text) rpmValue.textContent = text;
    rpmValue.dataset["rpm"] = rpm === null ? "" : String(rpm);
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);

  const offs = [store.subscribe(render), audio.onStatus(render), driver.onChange(render)];
  render();
  return () => {
    cancelAnimationFrame(frame);
    for (const off of offs) off();
  };
}
